import { Component, TemplateRef, ViewChild } from "@angular/core";
import { MainState } from "@app/store/state";
import { ContentTypeInterface } from "@core/interfaces/content-type.interface";
import { TogglePropertyInterface } from "@core/interfaces/toggle-property.interface";
import { CommonApiService } from "@core/services/api/classic/common/common-api.service";
import { DeviceService } from "@core/services/device.service";
import { NgbModal, NgbModalRef, NgbOffcanvas, NgbOffcanvasRef } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, take } from "rxjs/operators";

import { EquipmentFollowOptionsModalComponent } from "../equipment-follow-options-modal/equipment-follow-options-modal.component";

export interface EquipmentFollowOptionsConfig {
  forumTagContentType: ContentTypeInterface;
  equipmentContentType: ContentTypeInterface;
  itemId: number;
  forumTagId: number;
  itemName: string;
  itemBrandName: string;
  itemType: string;
  currentUserId: number;
}

export interface EquipmentFollowOptionsResult {
  followImages: boolean;
  followForum: boolean;
}

@Component({
  selector: "astrobin-equipment-follow-options",
  template: `
    <ng-template #offcanvasTemplate>
      <div class="offcanvas-header">
        <h5 class="offcanvas-title">{{ "Follow" | translate }} {{ getDisplayName() }}</h5>
        <button
          [attr.aria-label]="'Close' | translate"
          [disabled]="saving"
          (click)="cancel()"
          class="btn-close"
          type="button"
        ></button>
      </div>

      <div class="offcanvas-body">
        <p class="mb-4">{{ "Which kinds of notifications would you like to receive?" | translate }}</p>

        <div *ngIf="loading" class="text-center py-4">
          <fa-icon animation="spin" icon="spinner"></fa-icon>
        </div>

        <div *ngIf="!loading" class="follow-options">
          <div class="mb-3">
            <astrobin-toggle-button
              [disabled]="saving"
              [label]="'New images' | translate"
              [value]="followImages"
              (toggle)="followImages = $event"
            ></astrobin-toggle-button>
            <small class="d-block text-muted mt-1 ms-5">
              {{ "Get notified when new images are uploaded with this equipment" | translate }}
            </small>
          </div>

          <div class="mb-3">
            <astrobin-toggle-button
              [disabled]="saving"
              [label]="'New forum topics' | translate"
              [value]="followForum"
              (toggle)="followForum = $event"
            ></astrobin-toggle-button>
            <small class="d-block text-muted mt-1 ms-5">
              {{ "Get notified when new forum topics are posted about this equipment" | translate }}
            </small>
          </div>
        </div>

        <div *ngIf="error" class="alert alert-danger mt-3 mb-0">
          {{ error }}
        </div>

        <div class="mt-4 d-flex gap-2">
          <button [disabled]="saving" (click)="cancel()" class="btn btn-secondary flex-fill m-0" type="button">
            {{ "Cancel" | translate }}
          </button>
          <button [disabled]="loading || saving" (click)="save()" class="btn btn-primary flex-fill m-0" type="button">
            <fa-icon *ngIf="saving" class="me-2" animation="spin" icon="spinner"></fa-icon>
            {{ "Save" | translate }}
          </button>
        </div>
      </div>
    </ng-template>
  `
})
export class EquipmentFollowOptionsComponent extends BaseComponentDirective {
  @ViewChild("offcanvasTemplate", { static: true })
  offcanvasTemplate: TemplateRef<any>;
  followImages = false;
  followForum = false;
  loading = true;
  saving = false;
  error: string | null = null;
  // Current config and state
  private _currentConfig: EquipmentFollowOptionsConfig | null = null;
  private _currentOffcanvasRef: NgbOffcanvasRef | null = null;
  private _currentResolve: ((result: EquipmentFollowOptionsResult | null) => void) | null = null;
  private _existingImageFollowId: number | null = null;
  private _existingForumFollowId: number | null = null;

  constructor(
    public readonly store$: Store<MainState>,
    private readonly _modalService: NgbModal,
    private readonly _offcanvasService: NgbOffcanvas,
    private readonly _deviceService: DeviceService,
    private readonly _commonApiService: CommonApiService,
    private readonly _translateService: TranslateService
  ) {
    super(store$);
  }

  open(config: EquipmentFollowOptionsConfig): Promise<EquipmentFollowOptionsResult | null> {
    if (this._deviceService.isMobile()) {
      return this._openOffcanvas(config);
    } else {
      return this._openModal(config);
    }
  }

  getDisplayName(): string {
    if (!this._currentConfig) {
      return "";
    }
    const brand = this._currentConfig.itemBrandName || this._translateService.instant("DIY");
    return `${brand} ${this._currentConfig.itemName}`;
  }

  save(): void {
    if (!this._currentConfig) {
      return;
    }

    this.saving = true;
    this.error = null;

    const operations: Observable<TogglePropertyInterface | void>[] = [];

    // Handle image follow state
    if (this.followImages && !this._existingImageFollowId) {
      operations.push(
        this._commonApiService.createToggleProperty({
          propertyType: "follow",
          user: this._currentConfig.currentUserId,
          contentType: this._currentConfig.equipmentContentType.id,
          objectId: this._currentConfig.itemId
        })
      );
    } else if (!this.followImages && this._existingImageFollowId) {
      operations.push(this._commonApiService.deleteToggleProperty(this._existingImageFollowId));
    }

    // Handle forum follow state
    if (this.followForum && !this._existingForumFollowId) {
      operations.push(
        this._commonApiService.createToggleProperty({
          propertyType: "follow",
          user: this._currentConfig.currentUserId,
          contentType: this._currentConfig.forumTagContentType.id,
          objectId: this._currentConfig.forumTagId
        })
      );
    } else if (!this.followForum && this._existingForumFollowId) {
      operations.push(this._commonApiService.deleteToggleProperty(this._existingForumFollowId));
    }

    if (operations.length === 0) {
      this._closeWithResult({ followImages: this.followImages, followForum: this.followForum });
      return;
    }

    forkJoin(operations)
      .pipe(
        take(1),
        catchError(error => {
          this.error = this._translateService.instant(
            "An error occurred while saving your preferences. Please try again."
          );
          this.saving = false;
          return of(null);
        })
      )
      .subscribe(results => {
        if (results !== null) {
          this._closeWithResult({ followImages: this.followImages, followForum: this.followForum });
        }
      });
  }

  cancel(): void {
    this._closeWithResult(null);
  }

  private _openModal(config: EquipmentFollowOptionsConfig): Promise<EquipmentFollowOptionsResult | null> {
    const modalRef: NgbModalRef = this._modalService.open(EquipmentFollowOptionsModalComponent, {
      centered: true
    });

    const componentInstance = modalRef.componentInstance as EquipmentFollowOptionsModalComponent;
    Object.assign(componentInstance, config);

    return modalRef.result.catch(() => null);
  }

  private _openOffcanvas(config: EquipmentFollowOptionsConfig): Promise<EquipmentFollowOptionsResult | null> {
    return new Promise(resolve => {
      this._currentConfig = config;
      this._currentResolve = resolve;

      // Reset state
      this.followImages = false;
      this.followForum = false;
      this.loading = true;
      this.saving = false;
      this.error = null;
      this._existingImageFollowId = null;
      this._existingForumFollowId = null;

      this._currentOffcanvasRef = this._offcanvasService.open(this.offcanvasTemplate, {
        position: this._deviceService.offcanvasPosition()
      });

      this._currentOffcanvasRef.dismissed.subscribe(() => {
        this._closeWithResult(null);
      });

      this._loadCurrentFollowStates();
    });
  }

  private _loadCurrentFollowStates(): void {
    if (!this._currentConfig) {
      return;
    }

    const imageFollow$ = this._commonApiService.getToggleProperty({
      propertyType: "follow",
      user: this._currentConfig.currentUserId,
      contentType: this._currentConfig.equipmentContentType.id,
      objectId: this._currentConfig.itemId
    });

    const forumFollow$ = this._commonApiService.getToggleProperty({
      propertyType: "follow",
      user: this._currentConfig.currentUserId,
      contentType: this._currentConfig.forumTagContentType.id,
      objectId: this._currentConfig.forumTagId
    });

    forkJoin([imageFollow$, forumFollow$])
      .pipe(take(1))
      .subscribe(([imageFollow, forumFollow]) => {
        if (imageFollow) {
          this.followImages = true;
          this._existingImageFollowId = imageFollow.id;
        }
        if (forumFollow) {
          this.followForum = true;
          this._existingForumFollowId = forumFollow.id;
        }
        this.loading = false;
      });
  }

  private _closeWithResult(result: EquipmentFollowOptionsResult | null): void {
    if (this._currentOffcanvasRef) {
      this._currentOffcanvasRef.close();
      this._currentOffcanvasRef = null;
    }

    if (this._currentResolve) {
      this._currentResolve(result);
      this._currentResolve = null;
    }

    this._currentConfig = null;
  }
}
